import { useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION, type UpdateSubmittedApplicationVariables } from "../mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import type { ApplicationEducationDetails } from "../queries";
import {
  GRADUATION_MONTH_OPTIONS,
  GRADUATION_YEAR_OPTIONS,
  HIGHEST_LEVEL_OF_EDUCATION_OPTIONS,
  type HighestLevelOfEducation
} from "../../education";

type SubmittedEducationDetails = NonNullable<UpdateSubmittedApplicationVariables["input"]["educationDetails"]>;

type FieldValues = {
  schoolName: string;
  graduationMonth: string;
  graduationYear: string;
  highestLevelOfEducation: HighestLevelOfEducation | "";
};

interface Props {
  applicationId: string;
  educationDetails: ApplicationEducationDetails | null;
  onCancel: () => void;
  onSuccess: (educationDetails: ApplicationEducationDetails) => void;
  onError: (message: string) => void;
}

export default function EducationEditForm({ applicationId, educationDetails, onCancel, onSuccess, onError }: Props) {
  const { clearError } = useErrorBanner();
  const form = useForm<FieldValues>({
    defaultValues: {
      schoolName: educationDetails?.schoolName?.trim() ?? "",
      graduationMonth: educationDetails?.graduationMonth ?? "",
      graduationYear: educationDetails?.graduationYear ?? "",
      highestLevelOfEducation: educationDetails?.highestLevelOfEducation ?? ""
    }
  });

  const [updateApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }]
  });

  const onSubmit = async (values: FieldValues) => {
    const submittedEducationDetails: SubmittedEducationDetails = {
      schoolName: values.schoolName.trim(),
      graduationMonth: values.graduationMonth,
      graduationYear: values.graduationYear,
      highestLevelOfEducation: values.highestLevelOfEducation as HighestLevelOfEducation
    };

    try {
      const { data } = await updateApplication({
        variables: {
          input: {
            id: applicationId,
            educationDetails: submittedEducationDetails
          }
        }
      });

      const result = data?.updateSubmittedApplication;
      if (result?.success) {
        clearError();
        showSnackbar(result.message);
        onSuccess(submittedEducationDetails);
      } else {
        onError(result?.message ?? "Unable to update application");
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "Unable to update application");
    }
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
      <Controller
        control={form.control}
        name="highestLevelOfEducation"
        rules={{ required: "Highest level of education is required" }}
        render={({ field, fieldState }) => (
          <Select
            label="Highest Level of Education"
            required
            value={field.value}
            onSelect={field.onChange}
            options={HIGHEST_LEVEL_OF_EDUCATION_OPTIONS}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
          />
        )}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Controller
          control={form.control}
          name="graduationMonth"
          rules={{ required: "Graduation month is required" }}
          render={({ field, fieldState }) => (
            <Select
              label="Graduation Month"
              required
              value={field.value}
              onSelect={field.onChange}
              options={GRADUATION_MONTH_OPTIONS}
              error={!!fieldState.error}
              errorText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name="graduationYear"
          rules={{ required: "Graduation year is required" }}
          render={({ field, fieldState }) => (
            <Select
              label="Graduation Year"
              required
              value={field.value}
              onSelect={field.onChange}
              options={GRADUATION_YEAR_OPTIONS}
              error={!!fieldState.error}
              errorText={fieldState.error?.message}
            />
          )}
        />
      </div>
      <Controller
        control={form.control}
        name="schoolName"
        rules={{ required: "Name of institution is required" }}
        render={({ field, fieldState }) => (
          <TextField
            label="Name of Institution"
            required
            value={field.value}
            onChangeText={field.onChange}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
          />
        )}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button disabled={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
